import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { getMyEmployee } from "../api/employees";
import { normalizeWorkMode } from "../utils/workModeHelpers";

/**
 * Resolves the current employee's workMode.
 * `/employees/me` is authoritative so a stale login session (still OFFICE)
 * cannot hide remote attendance / WFH or bounce the user back to home
 * after HR switches them to HYBRID/REMOTE.
 */
export function useMyWorkMode() {
  const { user, patchUser } = useAuth();
  const { employeesByBranch } = useData();

  const profileWorkMode = useMemo(() => {
    const all = Object.values(employeesByBranch || {}).flat();
    const profile = all.find(
      (e) =>
        (user?.employeeId && e.id === user.employeeId) ||
        (user?.id && e.userId === user.id),
    );
    return profile?.workMode ? normalizeWorkMode(profile.workMode) : null;
  }, [employeesByBranch, user?.employeeId, user?.id]);

  const [workMode, setWorkMode] = useState(() =>
    normalizeWorkMode(user?.workMode),
  );
  const [resolved, setResolved] = useState(false);
  const fromApiRef = useRef(false);

  useEffect(() => {
    if (fromApiRef.current) return;
    setWorkMode(normalizeWorkMode(profileWorkMode || user?.workMode));
  }, [profileWorkMode, user?.workMode]);

  useEffect(() => {
    let cancelled = false;
    fromApiRef.current = false;
    setResolved(false);

    getMyEmployee()
      .then((me) => {
        if (cancelled || !me?.workMode) return;
        const mode = normalizeWorkMode(me.workMode);
        fromApiRef.current = true;
        setWorkMode(mode);
        if (user?.workMode !== mode) {
          patchUser?.({ workMode: mode });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setResolved(true);
      });

    return () => {
      cancelled = true;
    };
    // Re-fetch when the linked employee changes; patchUser is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.employeeId]);

  return { workMode, resolved };
}
